import { operators as o, modifiers as mod, common as c } from '@pendo/aggregations';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import { getBlank } from '@/net-promoter-system/nps-responses-table/utils';
import {
    HALF_RANGE_TO_MATCH_NPS_TO_FOLLOWUP,
    npsFilters,
    ML_SPECIAL_NPS_THEME_IDS,
    generalNpsThemeDisplayName,
} from '@/net-promoter-system/constants/nps.constants.js';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
const { noThemeResponses, textResponses } = npsFilters;

const { generalNpsTheme } = ML_SPECIAL_NPS_THEME_IDS;

type buildColumn = {
    columnLabel: string;
    columnName: string;
    fixed?: boolean;
    functionName: string;
    label: string;
    prop: string;
    sortable?: boolean;
    target?: string;
    type?: string;
    immutable: boolean;
    followUp?: boolean;
    pollId?: string;
    width?: number;
    minWidth?: number;
    schema?: string;
    guideId?: string;
    showOverflow?: string;
};

type filter = {
    id: string;
};

export interface NpsResponsesConfig {
    appId: c.AppIdType;
    blacklist: c.BlacklistTypes;
    channelId?: 'inAppOnly' | 'emailOnly' | 'inAppPlusEmail';
    columns: buildColumn[];
    filterExcludedResponses: boolean;
    searchFilter?: {
        value: string;
        prop: string[];
    } | null;
    guideId: string;
    includeAnonymous: string;
    isCsv?: boolean;
    limit: number;
    languageCode?: string;
    pollId: string;
    followUpPollId?: string | null;
    segmentId: string | null;
    selectedAccount?: c.AccountFilterAggInput;
    sort: string | Array<string> | null;
    select?: Record<string, string> | Array<string | Record<string, string>>;
    timeSeries: c.TimeSeries;
    hasNpsThemesFlag: boolean;
    localFilters?: Record<string, filter[]>;
    predictionsFromDate?: Date;
}

export interface BuildProps {
    config: NpsResponsesConfig;
    blacklist?: c.BlacklistTypes;
    timeSeries?: c.TimeSeries;
    segmentId?: string | null;
    includeAnonymous?: string;
    selectedAccount?: c.AccountFilterAggInput;
}

const build = (params: BuildProps) => {
    const config = {
        ...params.config,
        ...(params.blacklist !== undefined && { blacklist: params.blacklist }),
        ...(params.timeSeries !== undefined && { timeSeries: params.timeSeries }),
        ...(params.segmentId !== undefined && { segmentId: params.segmentId }),
        ...(params.includeAnonymous !== undefined && { includeAnonymous: params.includeAnonymous }),
        ...(params.selectedAccount !== undefined && { selectedAccount: params.selectedAccount }),
    };
    const {
        appId,
        blacklist,
        channelId,
        columns = [],
        filterExcludedResponses,
        searchFilter = null,
        guideId,
        includeAnonymous,
        isCsv = false,
        limit,
        languageCode,
        pollId,
        followUpPollId,
        segmentId,
        selectedAccount,
        sort,
        select,
        timeSeries,
        hasNpsThemesFlag = false,
        localFilters = {
            npsThemesFilter: [],
            ratingFilter: [],
            responseTypeFilter: [],
        },
        predictionsFromDate,
    } = config;
    const { npsThemesFilter, ratingFilter, responseTypeFilter } = localFilters;
    const followUpResponseColumn = columns.find((column) => column.immutable && column.followUp);
    const primaryKeys = c.columnPrimaryKeys(columns) as string[];
    const includesParentAccount = includes(primaryKeys, 'parentAccountId');
    const npsThemesFilterIds = npsThemesFilter.map(({ id }) => id);
    const ratingFilterIds = ratingFilter.map(({ id }) => id);
    const responseTypeFilterId = responseTypeFilter.length === 1 ? responseTypeFilter[0].id : null;

    let columnPipeline = columns
        ? c.callColumnGeneratorsWithDefaults(
            columns.filter((column) => !column.immutable),
            { dateRange: timeSeries },
            primaryKeys,
        )
        : [];

    // callColumnGeneratorsWithDefaults can insert null values into the result arr
    // which will break the modifiers called below,
    // so we need to filter them out
    columnPipeline = columnPipeline.filter((pipeline) => pipeline !== null);

    let columnPipelineReqDetails: c.AggRequestDetails = {
        name: '',
        pipeline: columnPipeline as c.PipelineData[],
    };

    // need to call modifiers on only the columns due to the existence of a merge operator that uses the poll events source.
    columnPipelineReqDetails = isString(includeAnonymous)
        ? mod.identifiedModifier(columnPipelineReqDetails, includeAnonymous)
        : columnPipelineReqDetails;
    columnPipelineReqDetails = segmentId
        ? mod.segmentModifier(columnPipelineReqDetails, segmentId)
        : columnPipelineReqDetails;
    columnPipelineReqDetails = includesParentAccount
        ? (mod.parentAccountModifier(columnPipelineReqDetails) as c.AggRequestDetails)
        : columnPipelineReqDetails;
    columnPipelineReqDetails = appId ? mod.appIdModifier(columnPipelineReqDetails, appId) : columnPipelineReqDetails;

    return o.pipeline(
        o.sources.pollEvents({
            guideId,
            pollId,
            timeSeries,
            blacklist,
        }),
        o.segment(segmentId),
        o.languageFilter(languageCode),
        o.channelFilter(channelId),
        o.accountFilter(selectedAccount),
        o.identified(includeAnonymous),
        filterExcludedResponses && o.filter('excluded != true'),
        includesParentAccount && o.evaluate({ parentAccountId: 'parentAccountId(accountId)' }),
        o.merge(
            ['visitorId', 'accountId'],
            o.mappings({
                followUpResponses: 'responses',
            }),
            o.pipeline(
                o.sources.pollEvents({
                    guideId,
                    pollId: followUpResponseColumn?.pollId || followUpPollId,
                    blacklist,
                    timeSeries,
                }),
                o.sort('browserTime'),
                c.nestedEval('responseObj', {
                    pollResponse: 'pollResponse',
                    browserTime: 'browserTime',
                }),
                o.group(['visitorId', 'accountId'], o.groupField('responses', o.list('responseObj'))),
            ),
        ),
        o.unwind('followUpResponses', { keepEmpty: true }),
        o.evalExpression({
            followUpResponse: `if (followUpResponses.browserTime >= (browserTime - ${HALF_RANGE_TO_MATCH_NPS_TO_FOLLOWUP}) && followUpResponses.browserTime < (browserTime + ${HALF_RANGE_TO_MATCH_NPS_TO_FOLLOWUP}), followUpResponses.pollResponse, null)`,
        }),
        o.group(
            ['visitorId', 'accountId', 'parentAccountId', 'pollResponse', 'browserTime', 'type', 'excluded'],
            o.groupField('followUpResponse', { first: 'followUpResponse' }),
        ),
        o.channelFilter(channelId),
        hasNpsThemesFlag &&
            o.merge(
                ['visitorId', 'browserTime'],
                {
                    mappings: {
                        browserTime: 'timestamp',
                        npsThemesTemp: `map(theme, resolvedThemes.selected, if(theme.name == "${generalNpsTheme}", "${generalNpsThemeDisplayName()}", theme.displayName))`,
                        suggestedNpsThemesTemp: 'resolvedThemes.suggested',
                        selectedNpsThemesTemp: 'resolvedThemes.selected',
                        ...(isCsv
                            ? {}
                            : {
                                previousSuggestedNpsThemesTemp: 'resolvedThemes.previousSuggested',
                                lastUpdatedByUserAtTemp: 'lastUpdatedByUserAt',
                                lastUpdatedByMlAtTemp: 'lastUpdatedByMlAt',
                                firstAssignedThemeByMlAtTemp: 'firstAssignedThemeByMlAt',
                            }),
                    },
                },
                o.pipeline(
                    o.sources.npsPredictions({
                        guideId,
                    }),

                    /* for NPS Ai activity aggregation */
                    /* predictions that assigned by ml for the first time in last X days */
                    predictionsFromDate &&
                        o.filter(
                            `isNil(lastUpdatedByUserAt) &&
                            !isNil(lastUpdatedByMlAt) &&
                            !isNil(firstAssignedThemeByMlAt) &&
                            lastUpdatedByMlAt >= ${predictionsFromDate.getTime()}`,
                        ),
                ),
            ),
        o.evalExpression({
            npsThemes: 'if(isNil(npsThemesTemp), [], npsThemesTemp)',
            suggestedNpsThemes: 'if(isNil(suggestedNpsThemesTemp), [], suggestedNpsThemesTemp)',
            selectedNpsThemes: 'if(isNil(selectedNpsThemesTemp), [], selectedNpsThemesTemp)',
            ...(isCsv
                ? {}
                : {
                    previousSuggestedNpsThemes:
                          'if(isNil(previousSuggestedNpsThemesTemp), [], previousSuggestedNpsThemesTemp)',
                    lastUpdatedByUserAt: 'if(isNil(lastUpdatedByUserAtTemp), null, lastUpdatedByUserAtTemp)',
                    lastUpdatedByMlAt: 'if(isNil(lastUpdatedByMlAtTemp), null, lastUpdatedByMlAtTemp)',
                    firstAssignedThemeByMlAt:
                          'if(isNil(firstAssignedThemeByMlAtTemp), null, firstAssignedThemeByMlAtTemp)',
                }),
        }),
        /*  For AI activity agg: npsPredictions source was filtered by `lastUpdatedByMlAt > predictionsFromDate` and now
            after merge it with pollevents then we now have pollevents with lastUpdatedByMlAt equal null and we need to remove it
            from merged list (only poll events which have lastUpdatedByMlAt and is latter then predictionsFromDate) */
        hasNpsThemesFlag && predictionsFromDate && o.filter('!isNil(lastUpdatedByMlAt)'),
        o.accountFilter(selectedAccount),
        o.switchStatement(
            'channel',
            'type',
            { '==': 'ui:web:pollResponse', 'value': 'In-app' },
            { '==': 'ui:mobile:pollResponse', 'value': 'In-app' },
            { '==': 'ui:email:pollResponse', 'value': 'Email' },
        ),
        o.switchStatement(
            'responseGroup',
            'pollResponse',
            { 'value': 'Detractor', '<': 7 },
            { 'value': 'Passive', '>=': 7, '<': 9 },
            { 'value': 'Promoter', '>=': 9 },
        ),
        ...columnPipelineReqDetails.pipeline,
        sort ? o.sort(sort) : undefined,
        !isCsv
            ? o.fork(
                o.pipeline(
                    o.unwind('selectedNpsThemes', { keepEmpty: true }),
                    o.select({
                        't.npsTheme': `if(!isNil(followUpResponse) && isNil(selectedNpsThemes.name), "${noThemeResponses.value}", selectedNpsThemes.displayName)`,
                        't.themeId': `if(!isNil(followUpResponse) && isNil(selectedNpsThemes.name), "${noThemeResponses.value}", selectedNpsThemes.name)`,
                        'responseGroup': 'responseGroup',
                    }),
                    o.group(
                        ['t.npsTheme', 't.themeId'],
                        ...(c
                            .groupFields({
                                't.promoters': o.countIf(null, 'responseGroup == `Promoter`'),
                                't.detractors': o.countIf(null, 'responseGroup == `Detractor`'),
                                't.passives': o.countIf(null, 'responseGroup == `Passive`'),
                                'responsesCount': o.count(null),
                            })
                            ?.filter((gf) => gf !== undefined) as Array<c.PipelineGroupField>),
                    ),
                    o.reduce({
                        counts: o.list('t'),
                        totalResponsesCount: o.sum('responsesCount'),
                    }),
                ),
                o.pipeline.apply(null, [
                    ...filtersParams(npsThemesFilterIds, ratingFilterIds, responseTypeFilterId, searchFilter),
                    o.limit(limit),
                    o.select(select),
                ]),
            )
            : /* for csv: no reduce action (calculate counts), no rows limit, make npsThemes a string if array contain only one theme */
            o.pipeline.apply(null, [
                ...filtersParams(npsThemesFilterIds, ratingFilterIds, responseTypeFilterId, searchFilter),
                o.evalExpression({
                    npsThemes: 'if(len(npsThemes) == 1, npsThemes[0], if(len(npsThemes) == 0, "", npsThemes))',
                }),
                o.select(select),
            ]),
    );
};

export const NpsPollResponsesAgg = {
    name: 'NpsResponsesTable',
    productArea: PRODUCT_AREAS.NPS,
    subProductArea: SUB_PRODUCT_AREAS.NPS_RESULTS,
    response: ({ config }: BuildProps) => {
        const { isCsv, columns } = config;
        if (isCsv) {
            return {
                location: 'gcs',
                mimeType: 'text/csv',
                fields: columns.map((column) => {
                    return {
                        field: column.prop,
                        label: column.label,
                    };
                }),
            };
        }

        return {
            location: 'request',
            mimeType: 'application/json',
        };
    },
    build,
};

export const NpsAiActivityAgg = {
    name: 'NpsAiActivity',
    productArea: PRODUCT_AREAS.NPS,
    subProductArea: SUB_PRODUCT_AREAS.NPS_RESULTS,
    responseFormat: 'rows',
    build,
};

function filtersParams (
    npsThemesFilterIds: string[],
    ratingFilterIds: string[],
    responseTypeFilterId: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchFilter: any,
) {
    return [
        npsThemesFilterIds.length > 0 &&
            o.filter(
                npsThemesFilterIds
                    .map((npsTheme) => {
                        const isBlank = npsTheme === getBlank();
                        if (isBlank) return 'isNil(followUpResponse)';

                        return !npsTheme
                            ? 'isNil(selectedNpsThemes)'
                            : `if("${npsTheme}" == "${noThemeResponses.value}", !isNil(followUpResponse) && len(selectedNpsThemes) == 0, contains(map(selectedNpsThemes, name),"${npsTheme}"))`;
                    })
                    .join(' || '),
            ),
        ratingFilterIds.length > 0 &&
            o.filter(
                ratingFilterIds
                    .map((rating) => {
                        return `pollResponse == ${rating}`;
                    })
                    .join(' || '),
            ),
        !!responseTypeFilterId &&
            o.filter(
                responseTypeFilterId === textResponses.value ? '!isNil(followUpResponse)' : 'isNil(followUpResponse)',
            ),
        searchFilter &&
            searchFilter.value.length > 0 &&
            o.filter(
                searchFilter.prop
                    .reduce((acc: string[], prop: string) => {
                        if (prop === 'npsThemes') {
                            acc.push(
                                `contains(map(theme, npsThemes, contains(toLowerCase(theme), toLowerCase("${searchFilter.value}"))), true)`,
                            );
                        } else {
                            acc.push(`contains(toLowerCase(${prop}), toLowerCase("${searchFilter.value}"))`);
                        }

                        return acc;
                    }, [])
                    .join(' || '),
            ),
    ];
}
